import React from 'react';
import { BsCaretUpFill } from 'react-icons/bs';

import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

import { format } from 'utils/format';
import { totalConsumptionKwh } from 'utils/invoiceConsumption';
import { TermInfo } from 'components/term-info/term-info.component';

import {
  Subtitle,
  Title,
  Value,
  Rate,
  CosumerUnitsDigestContainer,
} from './digest-card.styles';

interface DigestCardProps {
  invoiceList: Invoice[];
  prevInvoiceList?: Invoice[];
  // UCs com as séries de geração/injeção — autoconsumo da "Energia total"
  consumerUnitList?: ConsumerUnit[];
}

const calcVariation = (current: number, previous: number): number | null => {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
};

/**
 * Variação contra o período anterior de mesmo tamanho (out/2026, revisão de
 * UX): diz a referência ("vs. período anterior"), usa vírgula decimal e a cor
 * pelo SENTIDO — conta/consumo subindo é ruim (vermelho), economia subindo é
 * bom (verde). Antes "▲ 898.0%" saía em laranja sem referência, a mesma cor de
 * "economia subiu".
 */
const VariationBadge = ({
  variation,
  upIsGood,
}: {
  variation: number | null;
  upIsGood: boolean;
}) => {
  if (variation === null) return null;
  const isUp = variation >= 0;
  const good = isUp === upIsGood;
  return (
    <Rate
      className={good ? 'positive' : 'negative'}
      title="Comparado ao período anterior de mesmo tamanho"
    >
      <BsCaretUpFill className={isUp ? '' : 'rotate'} />
      {format.percentage(Math.abs(variation) / 100).replace(/,00$/, '')}%
      <span className="ref">vs. período anterior</span>
    </Rate>
  );
};

export const DigestCard = ({
  invoiceList,
  prevInvoiceList,
  consumerUnitList = [],
}: DigestCardProps) => {
  const unitById = new Map(consumerUnitList.map((uc) => [uc.id, uc]));
  const getTotalInvoiceValue = (list: Invoice[]) =>
    list.reduce((acc, item) => acc + item.invoiceValue, 0);

  const getTotalConsumption = (list: Invoice[]) =>
    // Distribuidora + autoconsumo solar: o que foi consumido, de qualquer fonte
    list.reduce(
      (acc, item) =>
        acc + totalConsumptionKwh(item, unitById.get(item.consumerUnit.id)),
      0
    );

  const getTotalSaved = (list: Invoice[]) =>
    list.reduce(
      (acc, item) =>
        acc + (item.virtual?.virtualGenerationEnergy.economyGenerated || 0),
      0
    );

  const currentValue = getTotalInvoiceValue(invoiceList);
  const currentConsumption = getTotalConsumption(invoiceList);
  const currentSaved = getTotalSaved(invoiceList);

  // Só compara quando o período anterior tem ao menos tantas faturas quanto o
  // atual — período anterior incompleto gerava "+898%" sem significado.
  const comparable =
    !!prevInvoiceList && prevInvoiceList.length >= invoiceList.length;

  const prevValue =
    prevInvoiceList && comparable ? getTotalInvoiceValue(prevInvoiceList) : null;
  const prevConsumption =
    prevInvoiceList && comparable ? getTotalConsumption(prevInvoiceList) : null;
  const prevSaved =
    prevInvoiceList && comparable ? getTotalSaved(prevInvoiceList) : null;

  return (
    <CosumerUnitsDigestContainer className="p-4">
      <Title className="mb-4">Resumo</Title>
      <Subtitle>
        Valor total das faturas
        <TermInfo term="valorTotalFaturas" />
      </Subtitle>
      <div className="mb-4">
        <Value>R$ {format.money(currentValue || 0)}</Value>
        <VariationBadge
          upIsGood={false}
          variation={
            prevValue !== null ? calcVariation(currentValue, prevValue) : null
          }
        />
      </div>
      <Subtitle>
        Energia Total Consumida
        <TermInfo term="energiaTotalConsumida" />
      </Subtitle>
      <div className="mb-4">
        <Value>{format.energy(currentConsumption || 0)} kWh</Value>
        <VariationBadge
          upIsGood={false}
          variation={
            prevConsumption !== null
              ? calcVariation(currentConsumption, prevConsumption)
              : null
          }
        />
      </div>
      <Subtitle>
        Valor economizado
        <TermInfo term="valorEconomizado" />
      </Subtitle>
      <div className="mb-4">
        <Value>R$ {format.money(currentSaved || 0)}</Value>
        <VariationBadge
          upIsGood
          variation={
            prevSaved !== null ? calcVariation(currentSaved, prevSaved) : null
          }
        />
      </div>
    </CosumerUnitsDigestContainer>
  );
};
